import React, { useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowLeft, CheckCircle2, Loader2 } from "lucide-react";

export default function ForgotPassword() {
    const [email, setEmail] = useState("");
    const [sent, setSent] = useState(false);
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    const onSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError("");
        try {
            await api.post("/auth/forgot-password", { email: email.trim().toLowerCase() });
            setSent(true);
        } catch (err) {
            setError(formatApiError(err));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="flex min-h-screen items-center justify-center bg-zinc-50 px-4" data-testid="forgot-page">
            <div className="w-full max-w-sm rounded-lg border border-zinc-200 bg-white p-8">
                <Link
                    to="/login"
                    className="mb-6 inline-flex items-center gap-1.5 text-xs text-zinc-500 hover:text-zinc-900"
                    data-testid="forgot-back-link"
                >
                    <ArrowLeft className="h-3 w-3" strokeWidth={1.5} /> Back to sign in
                </Link>
                <h1 className="font-display text-xl font-semibold tracking-tight">Forgot password</h1>
                <p className="mt-1 text-sm text-zinc-500">
                    Enter your work email and we will send you a reset link.
                </p>

                {sent ? (
                    <div className="mt-6 rounded-md border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-800" data-testid="forgot-success">
                        <CheckCircle2 className="mb-2 h-4 w-4" strokeWidth={1.5} />
                        If an account exists for <strong>{email}</strong>, a reset link has been sent.
                    </div>
                ) : (
                    <form onSubmit={onSubmit} className="mt-6 space-y-4" data-testid="forgot-form">
                        <div className="space-y-1.5">
                            <Label htmlFor="email" className="text-xs font-medium">
                                Work email
                            </Label>
                            <Input
                                id="email"
                                type="email"
                                required
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                data-testid="forgot-email"
                                className="h-10"
                            />
                        </div>
                        {error && (
                            <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
                                {error}
                            </div>
                        )}
                        <Button type="submit" className="w-full h-10" disabled={loading} data-testid="forgot-submit">
                            {loading && <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" strokeWidth={1.5} />}
                            Send reset link
                        </Button>
                    </form>
                )}
            </div>
        </div>
    );
}
